import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Plus, ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getPage, updatePage, SECTION_TYPES } from "@/lib/adminCmsApi";
import AdminShell from "@/components/admin/AdminShell";
import SectionEditor from "@/components/admin/SectionEditor";
import SortableList from "@/components/admin/SortableList";

export default function PageEditor() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    getPage(pageId).then(setPage).catch(() => toast.error("Failed to load page")).finally(() => setLoading(false));
  }, [pageId]);

  const save = async () => {
    if (!page) return;
    setSaving(true);
    try {
      const payload = {
        slug: page.slug, title: page.title,
        meta_title: page.meta_title || "", meta_description: page.meta_description || "",
        sections: (page.sections || []).map((s, i) => ({ ...s, order: i })),
        is_homepage: !!page.is_homepage, status: page.status || "published",
      };
      const updated = await updatePage(pageId, payload);
      setPage(updated);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const updateSection = (idx, newData) => {
    const sections = [...(page.sections || [])];
    sections[idx] = { ...sections[idx], data: newData };
    setPage({ ...page, sections });
  };

  const moveSection = (idx, dir) => {
    const sections = [...(page.sections || [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= sections.length) return;
    [sections[idx], sections[ni]] = [sections[ni], sections[idx]];
    setPage({ ...page, sections });
  };

  const toggleSection = (idx) => {
    const sections = [...(page.sections || [])];
    sections[idx] = { ...sections[idx], enabled: !sections[idx].enabled };
    setPage({ ...page, sections });
  };

  const removeSection = (idx) => {
    if (!window.confirm("Remove this section?")) return;
    const sections = [...(page.sections || [])];
    sections.splice(idx, 1);
    setPage({ ...page, sections });
  };

  const addSection = (type) => {
    const newSection = {
      id: `tmp-${Date.now()}`, type, order: page.sections?.length || 0,
      data: {}, enabled: true,
    };
    setPage({ ...page, sections: [...(page.sections || []), newSection] });
    setPickerOpen(false);
  };

  // Ensure every section has a stable id for dnd-kit
  const sectionsWithIds = (page?.sections || []).map((s, i) => ({
    ...s, id: s.id || `tmp-${i}-${s.type}`,
  }));
  const ids = sectionsWithIds.map((s) => s.id);

  const onReorder = (newSections) => {
    setPage({ ...page, sections: newSections });
  };

  if (loading) return <AdminShell title="Edit Page"><div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div></AdminShell>;
  if (!page) return <AdminShell title="Edit Page"><div>Page not found</div></AdminShell>;

  return (
    <AdminShell title={`Edit · ${page.title}`}>
      <div className="mb-6 flex items-center justify-between">
        <Link to="/admin/pages" className="inline-flex items-center gap-2 text-ags-text-muted hover:text-[#0E2A57] font-mono text-[10px] uppercase tracking-[0.22em]">
          <ArrowLeft size={14} /> Back to pages
        </Link>
        <button onClick={save} disabled={saving} data-testid="save-page"
                className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] disabled:opacity-50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>

      {/* Page metadata */}
      <div className="bg-white border border-[#0E2A57]/15 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-2">Title</span>
          <input type="text" value={page.title} onChange={(e) => setPage({ ...page, title: e.target.value })}
                 className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-2">URL slug</span>
          <input type="text" value={page.slug} disabled={page.is_homepage} onChange={(e) => setPage({ ...page, slug: e.target.value })}
                 className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none font-mono text-sm disabled:bg-ags-cream" />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-2">SEO Title</span>
          <input type="text" value={page.meta_title || ""} onChange={(e) => setPage({ ...page, meta_title: e.target.value })}
                 className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-2">Status</span>
          <select value={page.status || "published"} onChange={(e) => setPage({ ...page, status: e.target.value })}
                  className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </label>
        <label className="block md:col-span-2">
          <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-2">SEO Description</span>
          <textarea value={page.meta_description || ""} rows={2} onChange={(e) => setPage({ ...page, meta_description: e.target.value })}
                    className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
        </label>
      </div>

      {/* Sections list */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg">Sections ({page.sections?.length || 0})</h2>
        <button onClick={() => setPickerOpen(!pickerOpen)}
                className="inline-flex items-center gap-2 bg-[#2BB6E5] text-ags-ink hover:bg-[#BEE7F5] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em]">
          <Plus size={14} /> Add Section
        </button>
      </div>

      {pickerOpen && (
        <div className="bg-white border border-[#0E2A57]/15 p-6 mb-6 grid grid-cols-2 md:grid-cols-4 gap-2">
          {SECTION_TYPES.map((t) => (
            <button key={t.value} onClick={() => addSection(t.value)}
                    className="text-left bg-ags-cream hover:bg-[#BEE7F5] border border-[#0E2A57]/10 px-3 py-3 text-sm transition-colors">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0E2A57]">{t.value}</div>
              <div className="mt-1 font-bold text-ags-ink">{t.label}</div>
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <SortableList
          ids={ids}
          items={sectionsWithIds}
          onReorder={onReorder}
          renderItem={(s, idx) => (
            <div data-testid={`section-${idx}`} className="bg-white border border-[#0E2A57]/15">
              <div className="px-5 py-3 flex items-center gap-3 border-b border-ags-hairline pl-10">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55 w-8">{String(idx + 1).padStart(2, "0")}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] bg-[#0E2A57] text-white px-2 py-1">{s.type}</span>
                <button onClick={() => setEditingSection(editingSection === idx ? null : idx)}
                        className="font-heading font-bold text-ags-ink hover:underline flex-1 text-left">
                  {SECTION_TYPES.find((t) => t.value === s.type)?.label || s.type}
                </button>
                {!s.enabled && <span className="text-xs text-ags-text-muted">DISABLED</span>}
                <div className="flex items-center gap-1">
                  <button onClick={() => moveSection(idx, -1)} className="p-1.5 hover:bg-ags-cream" title="Up"><ChevronUp size={14} /></button>
                  <button onClick={() => moveSection(idx, 1)} className="p-1.5 hover:bg-ags-cream" title="Down"><ChevronDown size={14} /></button>
                  <button onClick={() => toggleSection(idx)} className="px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] border border-[#0E2A57]/30 hover:bg-ags-cream">{s.enabled !== false ? "On" : "Off"}</button>
                  <button onClick={() => removeSection(idx)} className="p-1.5 hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
              {editingSection === idx && (
                <div className="p-5 bg-ags-cream">
                  <SectionEditor type={s.type} data={s.data || {}} onChange={(d) => updateSection(idx, d)} />
                </div>
              )}
            </div>
          )}
        />
      </div>
    </AdminShell>
  );
}
